import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { useApi } from "./useApi";
import { useSessionSetAtom } from "../useSessionSetAtom";
import { createReadGuard } from "./_helpers/readGuard";
import { assertArrayPayload } from "@utils/apiPayload";
import type {
    CreateLegalClassRestrictionResponse,
    LegalClassRestriction,
    LegalClassRestrictionRequest,
} from "@models/legalClassRestriction";
import {
    legalClassRestrictionsAtom,
    isProcessingLegalClassRestrictionsAtom,
} from "@/store/legalClassRestrictionAtoms";

const LEGAL_CLASS_RESTRICTIONS_ENDPOINT = "/legalClassRestrictions";

// Which read may publish to legalClassRestrictionsAtom (#407 follow-up 9).
// Module-scope, like useProductApi's: the atom is module-scope (the app mounts
// no Jotai Provider), so only a sequence answers "has a newer read taken
// over?". Without it, a save's re-read behind a slower read could land first
// and be overwritten by the older list, putting back a rule just changed.
// Latest-only (`isLatest`).
const restrictionsReads = createReadGuard();

export const useLegalClassRestrictionApi = () => {
    const { callApi } = useApi();
    // Session-bound: an answer landing after sign-out must not refill it.
    const setRestrictions = useSessionSetAtom(legalClassRestrictionsAtom);
    const setIsProcessing = useSetAtom(isProcessingLegalClassRestrictionsAtom);

    // GET returns a flat array — this endpoint has no paging envelope.
    const getLegalClassRestrictions = useCallback(async (): Promise<
        LegalClassRestriction[]
    > => {
        const { isLatest } = restrictionsReads.begin();

        const data = await callApi<LegalClassRestriction[]>(
            LEGAL_CLASS_RESTRICTIONS_ENDPOINT,
            {
                // Asked when the failure lands, not when the read starts: a
                // read a newer one has superseded says nothing, or the screen
                // showing the newer rules would toast a failure it does not
                // have. The latest read's failure toasts as before. useApi
                // asks this about a network failure (no response) too, so a
                // superseded read that never reached the server is quiet.
                skipErrorNotification: () => !isLatest(),
            },
        );

        // Checked, not assumed. If this endpoint ever gains the
        // { items, totalCount } envelope the other admin lists use, the
        // page's `restrictions.length` would be undefined and the screen
        // would answer "no restrictions are configured — every legal class
        // can currently ship everywhere" on a SUCCESSFUL response. On a
        // compliance screen an unreadable answer has to fail loudly.
        assertArrayPayload(LEGAL_CLASS_RESTRICTIONS_ENDPOINT, data);

        if (isLatest()) setRestrictions(data);

        return data;
    }, [callApi, setRestrictions]);

    const createLegalClassRestriction = useCallback(
        async (
            data: LegalClassRestrictionRequest,
        ): Promise<CreateLegalClassRestrictionResponse> => {
            setIsProcessing(true);

            try {
                return await callApi<CreateLegalClassRestrictionResponse>(
                    LEGAL_CLASS_RESTRICTIONS_ENDPOINT,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                    },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // Full-replace on the server, so the dialog sends every field.
    const updateLegalClassRestriction = useCallback(
        async (
            id: string,
            data: LegalClassRestrictionRequest,
        ): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(`${LEGAL_CLASS_RESTRICTIONS_ENDPOINT}/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    const deleteLegalClassRestriction = useCallback(
        async (id: string): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(`${LEGAL_CLASS_RESTRICTIONS_ENDPOINT}/${id}`, {
                    method: "DELETE",
                    contentType: "none",
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    return {
        getLegalClassRestrictions,
        createLegalClassRestriction,
        updateLegalClassRestriction,
        deleteLegalClassRestriction,
    };
};
